import moment from 'moment';

import { DateRange, DateFilters, CopyData } from '../../types';
import { fetchCall } from '../../services/api';
import { store } from '../../redux/store';

export const fetchAvailableRanges = async (): Promise<Array<DateRange>> => {
  try {
    const apiUrl = store.getState().config.react_app_api_base_url;
    const ranges = await fetchCall(`${apiUrl}/client/metadata/available`);

    return ranges;
  } catch (error: any) {
    throw new Error(error);
  }
};

/**
 * Returns an array of string date values from the current month
 * based on arguments provided
 * @param numberOfMonths
 * @param includeLatestMonth
 * @returns Array<number>
 */
const returnDateStringArray = (
  numberOfMonths: number,
  rangeLimit?: any,
  includeLatestMonth?: boolean
): Array<string> => {
  const stringArray = [];

  for (
    let i = includeLatestMonth ? 0 : 1;
    (includeLatestMonth ? i < numberOfMonths : i <= numberOfMonths) && i < rangeLimit.length;
    i++
  ) {
    stringArray.push(moment(rangeLimit[i].startDate).startOf('month').format('MMMM YYYY'));
  }
  // console.log(includeLatestMonth ,numberOfMonths, stringArray);
  return stringArray;
};

/**
 * Returns object array of predefined filter elements based on
 * conditional logic
 * @param formattedRange
 * @returns Array<DateFilters>
 */
export const returnFormattedOptions = (
  range: Array<DateRange> | null,
  copyData: CopyData | null,
  isNotesScreen?: boolean
): Array<DateFilters> | null => {
  if (!range) return null;

  const formattedRange = range.map((obj) => moment(obj.startDate).format('MMMM YYYY'));

  // uncomment below for testing logic
  // const formattedRange = ["March 2021", "February 2021", "January 2021" ];

  const dateFilters = isNotesScreen
    ? [
        copyData?.dates_this_month,
        copyData?.dates_last_month,
        copyData?.dates_last_3_month,
        copyData?.dates_last_6_month,
        copyData?.dates_last_12_month,
        copyData?.dates_year_to_date,
        copyData?.dates_custom_range
      ]
    : [
        copyData?.dates_last_month,
        copyData?.dates_last_3_month,
        copyData?.dates_last_6_month,
        copyData?.dates_last_12_month,
        copyData?.dates_year_to_date,
        copyData?.dates_custom_range
      ];

  const currentMonthAvailable = moment(range[0].startDate).format('MMM') === moment().format('MMM');
  // const currentMonthAvailable = true; // uncomment this check to test the case if current Month data is available

  return dateFilters.map((filter: any) => {
    // if (filter === copyData?.dates_this_month) {
    //   return {
    //     option: filter,
    //     disabled: !formattedRange.includes(moment(range[0].startDate).startOf('month').format('MMMM YYYY'))
    //   };
    // }

    if (filter === copyData?.dates_last_month) {
      return {
        option: filter,
        disabled: (() => {
          if (formattedRange.length < 1) return true;
          const dateArray = returnDateStringArray(1, range, !currentMonthAvailable);
          return !dateArray.every((date: string) => formattedRange.includes(date));
        })()
      };
    }

    if (filter === copyData?.dates_last_3_month) {
      return {
        option: filter,
        disabled: (() => {
          if (formattedRange.length < 3) return true;
          const dateArray = returnDateStringArray(3, range, !currentMonthAvailable);
          return !dateArray.every((date: string) => formattedRange.includes(date));
        })()
      };
    }

    if (filter === copyData?.dates_last_6_month) {
      return {
        option: filter,
        disabled: (() => {
          if (formattedRange.length < 6) return true;
          const dateArray = returnDateStringArray(6, range, !currentMonthAvailable);
          return !dateArray.every((date: string) => formattedRange.indexOf(date) > -1);
        })()
      };
    }

    if (filter === copyData?.dates_last_12_month) {
      return {
        option: filter,
        disabled: (() => {
          if (formattedRange.length < 12) return true;
          const dateArray = returnDateStringArray(12, range, !currentMonthAvailable);
          return !dateArray.every((date: string) => formattedRange.indexOf(date) > -1);
        })()
      };
    }

    if (filter === copyData?.dates_year_to_date) {
      return {
        option: filter,
        disabled: (() => {
          const currentMonth = moment(range[0].startDate);
          const firstMonth = moment().startOf('year');

          const monthDiff = currentMonth.diff(firstMonth, 'months');
          const dateArray = returnDateStringArray(monthDiff + 1, range, true);

          return !dateArray.every((date: string) => formattedRange.indexOf(date) > -1);
        })()
      };
    }

    return {
      option: filter,
      disabled: false
    };
  });
};
